import { useForm } from '@nerdgeschoss/react-use-form-library';
import React, { useEffect, useState } from 'react';
import { Button } from '../../../../../components/Button/Button';
import { PermissionButton } from '../../../../../components/Button/PermissionButton';
import { Card } from '../../../../../components/Card/Card';
import { FormSelect } from '../../../../../components/Form/Select/FormSelect';
import { Icon } from '../../../../../components/Icon/Icon';
import { KeyValueTable } from '../../../../../components/KeyValueTable/KeyValueTable';
import { LoadingPage } from '../../../../../components/LoadingPage/LoadingPage';
import { ControlledModal } from '../../../../../components/Modal/ControlledModal';
import { Stack } from '../../../../../components/Stack/Stack';
import { graphql } from '../../../../../generated';
import { t } from '../../../../../locales/translate';
import { useTask } from '../../../../../model/data';
import { Event, EventEdit } from '../../../../../model/event';
import { runTicketPreviewFullPdfExport } from '../../../../../model/exports';
import { useQueryHandler } from '../../../../../model/graphql';
import { getTicketPreview } from '../../../../../model/ticket';
import { dig, parseBytes, useReloadKey } from '../../../../../util/util';
import { TicketForm } from './TicketForm';
import { Box } from '../../../../../components/Box/box';
import { Spinner } from '../../../../../components/Spinner/Spinner';
import { compact } from 'lodash';
import { Passbook } from './design/passbook';
import { PublicTransport } from './public_transport/public_transport';
import { useModalPresenter } from '../../../../../components/Modal/PresentedModal';
import { RerenderTickets } from './design/rerender_tickets';
import { TicketLayouts } from './ticket_layout/ticket_layouts';

const PREVIEW_TICKET = graphql(`
  query GetDataForTicketPreview($id: ID!) {
    event(id: $id) {
      id
      permissions {
        id
        rerenderTickets
      }
      productVariants {
        nodes {
          id
          physical
          private
          local
          description
          pricingCategory {
            id
            name
          }
          ... on EventProductVariant {
            availableSections {
              nodes {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`);

export function Design({ event }: { event: Event }): JSX.Element {
  const [showEditModal, setShowEditModal] = useState(false);

  const [ticketPreviewUrl, setTicketPreviewUrl] = useState('');
  const [reloadCount, reloadPreview] = useReloadKey();

  const modal = useModalPresenter();

  const { data, loading } = useQueryHandler(PREVIEW_TICKET, {
    variables: { id: event.id },
  });
  const { model, fields, onSubmit } = useForm<{
    productVariantId: string;
    pricingCategoryId: string;
    venueSectionId: string;
  }>({
    model: {
      productVariantId: '',
      pricingCategoryId: '',
      venueSectionId: '',
    },
    onSubmit: ({ model }) => {
      getTicketPreview({
        eventId: event.id,
        productVariantId: model.productVariantId,
        venueSectionId: model.venueSectionId,
      }).then((res) => {
        setTicketPreviewUrl(parseBytes(res.preview, 'application/pdf'));
      });
    },
  });

  const exportTask = useTask({ handleError: true });

  const productVariants = compact(data?.event?.productVariants.nodes ?? []);
  const pricingCategoryOptions = productVariants
    .filter(
      (value, index, self) => self.findIndex((v) => v.pricingCategory?.id === value.pricingCategory?.id) === index
    )
    .map((e) => ({ value: e.pricingCategory?.id, label: e.pricingCategory?.name }));
  const productVariantOptions = productVariants
    .filter((e) => e.pricingCategory && model.pricingCategoryId === e.pricingCategory.id)
    .map((e) => ({
      value: e.id,
      label: e.physical
        ? t.model.ticket.hardProductVariant(e.description || '')
        : t.model.ticket.onlineProductVariant(e.description || ''),
    }));
  const selectedVariant = productVariants.find((e) => e.id === model.productVariantId);
  const venueSectionOptions =
    selectedVariant?.__typename === 'EventProductVariant'
      ? selectedVariant?.availableSections.nodes
          .map((e) => ({
            value: e.id,
            label: e.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];

  useEffect(() => {
    setTicketPreviewUrl('');

    getTicketPreview({
      eventId: event.id,
      productVariantId: model.productVariantId,
      venueSectionId: model.venueSectionId,
    }).then((res) => {
      setTicketPreviewUrl(parseBytes(res.preview, 'application/pdf'));
    });
  }, [event.id, reloadCount]);

  useEffect(() => {
    const firstNode = data?.event?.productVariants.nodes?.[0] ?? null;

    // Set default values
    if (firstNode && firstNode.__typename === 'EventProductVariant') {
      firstNode.pricingCategory?.id && fields.pricingCategoryId.onChange(firstNode.pricingCategory.id);
      fields.productVariantId.onChange(firstNode.id);
      fields.venueSectionId.onChange(firstNode.availableSections.nodes[0]?.id);
      reloadPreview();
    }
  }, [data]);

  return (
    <>
      <Stack size="lg">
        <Card
          title={t.general.generalInformation}
          actions={
            <PermissionButton
              name="edit"
              model={event}
              title={t.model.ticket.updateGeneralInformation}
              permission="update_base_attributes"
              onClick={() => setShowEditModal(true)}
            />
          }
        >
          <KeyValueTable
            labelWidth={300}
            items={[
              {
                key: `${t.model.ticket.ticketline} 1`,
                value: event.artistOverride || event.artist,
              },
              {
                key: `${t.model.ticket.ticketline} 2`,
                value: event.nameOverride || event.name,
              },
              {
                key: `${t.model.ticket.ticketline} 3`,
                value: event.ticketSupportLineOverride || event.ticketSupportLine,
              },
              {
                key: `${t.model.ticket.ticketline} ${t.general.headline}`,
                value: event.ticketHeadlineOverride || event.ticketHeadline,
              },
              {
                key: `${t.model.ticket.ticketline} ${t.general.footer}`,
                value: event.additionalInformationOverride || dig(event.additionalInformation, 'title'),
              },
              {
                key: t.model.ticket.personalIdentifierDisclaimer,
                value: event.meta?.personalIdentifierDisclaimer,
              },
              {
                key: 'logos',
                keyNode: <div className="logo-view__key">{t.model.logo._plural}</div>,
                value: (
                  <div className="logo-view__wrapper">
                    {event.logos.map((logo) => (
                      <img key={logo.id} className="logo-view__logo" src={logo.image.url} />
                    ))}
                  </div>
                ),
              },
              {
                key: t.model.ticket.headerImage,
                value: event.ticketHeaderImage && <img src={event.ticketHeaderImage.url} />,
              },
            ]}
          />
        </Card>
        <PublicTransport eventId={event.id} />
        <TicketLayouts eventId={event.id} />
        <Card
          title={t.model.ticket.ticketPreview}
          actions={
            <>
              {data?.event?.permissions.rerenderTickets && (
                <Icon
                  name="refresh--sm"
                  tooltip={t.model.ticket.rerender.title}
                  onClick={() => modal.present(RerenderTickets, { eventId: event.id })}
                />
              )}
              <Icon
                name="download--sm"
                tooltip={t.model.ticket.ticketPreviewFullPdf}
                loading={exportTask.loading}
                onClick={() =>
                  exportTask.run(
                    runTicketPreviewFullPdfExport({
                      eventId: event.id,
                    })
                  )
                }
              />
            </>
          }
        >
          <Stack size="sm">
            {loading ? (
              <Spinner />
            ) : (
              <Stack line>
                <FormSelect
                  {...fields.pricingCategoryId}
                  required
                  label={t.model.ticket.pricingCategories}
                  options={pricingCategoryOptions}
                  onChange={(val) => {
                    fields.pricingCategoryId.onChange(val || '');
                    const selectedVariant = productVariants.find((e) => e.pricingCategory?.id === val);
                    fields.productVariantId.onChange(selectedVariant?.id || '');
                    const availableVenueSections =
                      selectedVariant?.__typename === 'EventProductVariant'
                        ? selectedVariant?.availableSections.nodes
                        : [];
                    fields.venueSectionId.onChange(availableVenueSections[0]?.id);
                  }}
                />
                <FormSelect
                  {...fields.productVariantId}
                  required
                  label={t.model.ticket.productVariant}
                  options={productVariantOptions}
                  onChange={(val) => {
                    const variant = productVariants.find((e) => e.id === val);
                    if (variant?.__typename !== 'EventProductVariant') return;
                    fields.productVariantId.onChange(variant.id);
                    fields.venueSectionId.onChange(variant.availableSections.nodes[0]?.id);
                  }}
                />
                <FormSelect
                  {...fields.venueSectionId}
                  required
                  label={t.model.ticket.venueSection}
                  options={venueSectionOptions}
                />
                <Box>
                  <Button type="primary" title={t.model.ticket.preview} onClick={onSubmit} />
                </Box>
              </Stack>
            )}
            {ticketPreviewUrl ? (
              <iframe src={ticketPreviewUrl} width="100%" height="400" frameBorder="0" />
            ) : (
              <LoadingPage />
            )}
          </Stack>
        </Card>
        <Passbook productId={event.id} />
      </Stack>
      <ControlledModal<EventEdit>
        open={showEditModal}
        model={event as EventEdit}
        onClose={() => setShowEditModal(false)}
        title={t.model.ticket.updateTicketInfo}
        size="small"
      >
        {(controller) => (
          <TicketForm
            controller={controller}
            onUpdate={() => {
              reloadPreview();
            }}
          />
        )}
      </ControlledModal>
    </>
  );
}
